/* AthleteSocialLinks — iter454.4
 * ─────────────────────────────────────────────────────────────────────
 * Row of small circular social buttons under the profile card.
 * Reads from `athlete.social_links` (dict of platform → url) with a
 * legacy fallback to `athlete.instagram` (a bare handle).
 *
 * iter454.4 · Brand-tinted glows + X (formerly Twitter). Each platform
 * gets a soft outer shadow in its brand color so the row feels alive
 * without becoming garish. The Twitter bird icon has been retired —
 * anything stored as `twitter` on the athlete doc still renders (so
 * old data doesn't disappear) but it renders under the "x" brand:
 * X wordmark icon + X-brand fuchsia glow.
 */
import { Instagram, Youtube, Facebook, Linkedin, Globe, Music2, Twitch } from 'lucide-react';
import { toast } from 'sonner';
import { toSocialUrl } from '../../lib/socialUrl';

/* Minimalist X-brand icon (rebranded from Twitter). Uses currentColor
 * so the surrounding text/glow palette drives its color. */
const XIcon = ({ className = '', ...rest }) => (
  <svg
    viewBox="0 0 24 24"
    fill="currentColor"
    aria-hidden="true"
    className={className}
    {...rest}
  >
    <path d="M18.244 2H21.55l-7.223 8.257L23 22h-6.79l-5.32-6.955L4.8 22H1.492l7.73-8.834L1 2h6.923l4.808 6.354L18.244 2Zm-1.16 18h1.833L7.005 3.9H5.038l12.046 16.1Z" />
  </svg>
);

const ICON = {
  instagram: Instagram,
  // iter454.4 · Twitter is now X. Both keys hit the X icon so legacy
  // data doesn't lose its button but the branding stays current.
  twitter: XIcon,
  x: XIcon,
  youtube: Youtube,
  facebook: Facebook,
  linkedin: Linkedin,
  tiktok: Music2,
  threads: Music2,
  // Athletes with a Twitch handle should show it — many are streaming.
  twitch: Twitch,
  website: Globe,
};

/* Brand palette — text color + outer glow. Kept low-saturation on the
 * fill and higher-saturation on the shadow so the icons still read
 * clean on pure black while radiating their brand hue. */
const BRAND = {
  instagram: { color: '#f5a3d5', glow: 'rgba(237, 84, 149, 0.55)' }, // Jay wanted "lighter purple"
  x:         { color: '#ffffff', glow: 'rgba(180, 180, 200, 0.55)' }, // X is neutral / mono
  twitter:   { color: '#ffffff', glow: 'rgba(180, 180, 200, 0.55)' }, // legacy alias
  youtube:   { color: '#ff5c5c', glow: 'rgba(255, 0, 0, 0.55)' },
  facebook:  { color: '#7aa8ff', glow: 'rgba(24, 119, 242, 0.6)' },
  linkedin:  { color: '#7aa8ff', glow: 'rgba(10, 102, 194, 0.55)' },
  tiktok:    { color: '#c69cff', glow: 'rgba(155, 87, 255, 0.6)' }, // purple per Jay
  threads:   { color: '#dddddd', glow: 'rgba(255, 255, 255, 0.35)' },
  twitch:    { color: '#c69cff', glow: 'rgba(145, 70, 255, 0.55)' },
  website:   { color: '#a0e5ff', glow: 'rgba(56, 189, 248, 0.45)' }, // Jay: "website can be whatever"
};

const AthleteSocialLinks = ({ athlete, isOwner = false }) => {
  if (!athlete) return null;
  const raw = { ...(athlete.social_links || {}) };
  // Legacy: `athlete.instagram` was a bare handle (e.g. "patrickmahomes").
  if (athlete.instagram && !raw.instagram) {
    raw.instagram = athlete.instagram.startsWith('http')
      ? athlete.instagram
      : `https://instagram.com/${athlete.instagram.replace(/^@/, '')}`;
  }
  const entries = Object.entries(raw)
    .filter(([, v]) => v && typeof v === 'string')
    .map(([k, v]) => [k, toSocialUrl(k.toLowerCase() === 'x' ? 'twitter' : k.toLowerCase(), v)])
    .filter(([, v]) => v);
  if (!entries.length) return null;

  // iter454.5 · Owner tap-guard. When the athlete themself taps their
  // own social icon on the network profile, we intercept the click and
  // surface a toast telling them these values are populated by the
  // FLYT ID™ (their public /profile link builder) so they don't try to
  // edit them from the network page. Everyone else (fans, recruiters,
  // agents, and the owner in preview mode) gets normal outbound taps.
  const handleOwnerTap = (e) => {
    if (!isOwner) return;
    e.preventDefault();
    e.stopPropagation();
    toast.info('These are populated by your FLYT ID™ — make changes there so every link stays in sync.', {
      duration: 4500,
    });
  };

  return (
    <div className="mt-2 flex flex-wrap items-center gap-1.5" data-testid="athlete-social-links">
      {entries.map(([platform, url]) => {
        const key = platform.toLowerCase();
        const Icon = ICON[key] || Globe;
        const brand = BRAND[key] || BRAND.website;
        return (
          <a
            key={platform}
            href={url}
            target="_blank"
            rel="noopener noreferrer"
            onClick={handleOwnerTap}
            className="group inline-flex items-center justify-center w-7 h-7 rounded-full bg-white/[0.06] ring-1 ring-white/15 hover:bg-white/[0.12] transition"
            style={{
              boxShadow: `0 0 0 1px ${brand.glow}, 0 0 12px -2px ${brand.glow}`,
              color: brand.color,
            }}
            aria-label={key === 'twitter' ? 'x' : key}
            data-testid={`social-${key === 'twitter' ? 'x' : key}`}
          >
            <Icon className="w-3.5 h-3.5" />
          </a>
        );
      })}
    </div>
  );
};

export default AthleteSocialLinks;
